import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface HeatBarProps {
  temperature: number;
  className?: string;
}

function getTemperatureColor(temp: number): string {
  if (temp >= 75) return 'var(--color-hot)';
  if (temp >= 50) return 'var(--color-warm)';
  if (temp >= 25) return 'var(--color-cool)';
  return 'var(--color-cold)';
}

function getTemperatureLabel(temp: number, t: (key: string) => string): string {
  if (temp >= 75) return t('items:curve.warming');
  if (temp >= 50) return t('items:curve.warming');
  if (temp >= 25) return t('items:curve.cooling');
  return t('items:curve.cooling');
}

export function HeatBar({ temperature, className }: HeatBarProps) {
  const { t } = useTranslation();
  const color = useMemo(() => getTemperatureColor(temperature), [temperature]);
  const label = useMemo(() => getTemperatureLabel(temperature, t), [temperature, t]);
  const pct = Math.max(0, Math.min(100, temperature));

  return (
    <div
      className={className}
      role="meter"
      aria-label={label}
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        width: '100%',
        height: '4px',
        borderRadius: 'var(--radius-sm)',
        backgroundColor: 'var(--color-card-border)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${String(pct)}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius: 'var(--radius-sm)',
          transition: 'width var(--transition-normal), background-color var(--transition-normal)',
        }}
      />
    </div>
  );
}
